import { ChangeEvent, FormEvent, useState } from "react";
import {
  useUploadComplianceTrainingDocumentMutation,
} from "../../redux/api/complianceTraining";
import { ComplianceRosterResponse, ComplianceRosterRow, ComplianceTraining } from "../../common/ComplianceTraining";
import { toast } from "react-toastify";
import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  InputLabel,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface ComplianceTrainingUploadModalProps {
  rosterResponse: ComplianceRosterResponse | undefined;
}

const ComplianceTrainingUploadModal = ({
  rosterResponse,
}: ComplianceTrainingUploadModalProps) => {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<ComplianceRosterRow[]>([]);
  const [selectedTrainings, setSelectedTrainings] = useState<ComplianceTraining[]>([]);
  const [noDocumentRequired, setNoDocumentRequired] = useState(false);
  const [issueDate, setIssueDate] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [file, setFile] = useState<File>();

  const [uploadDocument] = useUploadComplianceTrainingDocumentMutation();

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    clearForm();
  };

  const clearForm = () => {
    setSelectedRows([]);
    setSelectedTrainings([]);
    setNoDocumentRequired(false);
    setIssueDate("");
    setExpirationDate("");
    setFile(undefined);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const mb = 1024 * 1024;
    if (!event.target.files || !event.target.files[0]) {
      setFile(undefined);
      return;
    }
    if (event.target.files[0].size > 20 * mb) {
      toast.error(<p>File is too big. Files must be 20MB or less.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }
    setFile(event.target.files[0]);
  };

  const toastError = (msg: string) =>
    toast.error(<p>{msg}</p>, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (selectedRows.length === 0) {
      toastError("Please select a mariner.");
      return;
    }
    if (selectedTrainings.length === 0) {
      toastError("Please select a training.");
      return;
    }
    const noDocValid = noDocumentRequired && selectedTrainings.every((t) => !t.documentRequired);
    if (!noDocValid && !file) {
      toastError("No valid file to upload.");
      return;
    }
    if (!expirationDate) {
      toastError("Please provide an expiration date.");
      return;
    }

    doUpload();
  };

  const doUpload = async () => {
    setUploading(true);
    const body = JSON.stringify({
      profileIds: selectedRows.map((r) => r.profileId),
      complianceTrainingIds: selectedTrainings.map((t) => t.id),
      issueDate: issueDate || undefined,
      expirationDate: expirationDate || undefined,
    });
    const data = new FormData();
    data.append("record", new Blob([body], { type: "application/json" }));
    if (file) data.append("file", file);
    const response = await uploadDocument(data);
    setUploading(false);
    if ("error" in response) {
      toast.error(<p>Failed to upload document. Please try again.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    } else {
      toast.success(<p>Training document uploaded successfully.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      handleClose();
    }
  };

  const rows = rosterResponse?.rows ?? [];
  const trainings = rosterResponse?.trainings ?? [];
  const SELECT_ALL_MARINER = { profileId: -1, firstName: "Select All", middleName: null, lastName: "" } as ComplianceRosterRow;

  return (
    <>
      <Button
        variant="contained"
        className="blue-button wd-auto ht-4px"
        onClick={handleOpen}
      >
        Upload
      </Button>
      <Dialog
        className="modal compliance-upload-dialog"
        open={open}
        onClose={handleClose}
        aria-labelledby="compliance-upload-title"
      >
        <div className="modal-header mb-2">
          <Typography id="compliance-upload-title" variant="h3">
            Upload Training Document
          </Typography>
          <Button onClick={handleClose} className="grey-link-button">
            <CloseIcon />
          </Button>
        </div>
        <form onSubmit={handleSubmit}>
          <div
            className="form-narrow"
            id="compliance-upload-fields"
            style={{ textOverflow: "ellipsis" }}
          >
            <div>
              <InputLabel id="marinerLabel">Mariner</InputLabel>
              <Autocomplete
                multiple
                disablePortal
                disableCloseOnSelect
                options={[SELECT_ALL_MARINER, ...rows]}
                value={selectedRows}
                onChange={(_, value) => {
                  if (value.some((v) => v.profileId === -1)) {
                    setSelectedRows(selectedRows.length === rows.length ? [] : rows);
                  } else {
                    setSelectedRows(value);
                  }
                }}
                getOptionLabel={(row) =>
                  row.profileId === -1
                    ? "---Select All---"
                    : [row.firstName, row.middleName, row.lastName]
                        .filter(Boolean)
                        .join(" ")
                }
                isOptionEqualToValue={(opt, val) =>
                  opt.profileId === val.profileId
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select Mariners" />
                )}
              />
            </div>
            <div>
              <InputLabel id="trainingLabel">Training</InputLabel>
              <Autocomplete
                multiple
                disablePortal
                disableCloseOnSelect
                options={trainings}
                value={selectedTrainings}
                onChange={(_, value) => {
                  setSelectedTrainings(value);
                  if (!value.every((t) => !t.documentRequired)) setNoDocumentRequired(false);
                }}
                getOptionLabel={(t) => t.name}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select Trainings" />
                )}
              />
            </div>
            <label htmlFor="issueDate">
              <span>Issue Date:</span>
              <input
                type="date"
                id="issueDate"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
              />
            </label>
            <div>
              <label htmlFor="expirationDate">
                <span>Expiration Date:</span>
                <input
                  type="date"
                  id="expirationDate"
                  value={expirationDate}
                  onChange={(e) => setExpirationDate(e.target.value)}
                />
              </label>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginTop: "3%" }}>
            <label htmlFor="fileUpload">
              <span>Upload File:</span>
              <input
                type="file"
                id="fileUpload"
                onChange={handleFileChange}
                disabled={noDocumentRequired}
              />
            </label>
            {selectedTrainings.length > 0 && selectedTrainings.every((t) => !t.documentRequired) && (
              <label htmlFor="noDocumentRequired" style={{ display: "flex", flexDirection: "column", alignItems: "center", whiteSpace: "nowrap" }}>
                <span>No Document Required:</span>
                <Checkbox
                  id="noDocumentRequired"
                  name="noDocumentRequired"
                  checked={noDocumentRequired}
                  onChange={(e) => {
                    setNoDocumentRequired(e.target.checked);
                    if (e.target.checked) setFile(undefined);
                  }}
                />
              </label>
            )}
          </div>
          <div className="modal-button">
            <Button className="outline-button-small" onClick={handleClose}>
              Cancel
            </Button>
            <input
              className="blue-button-small"
              type="submit"
              value="Upload"
              disabled={uploading}
            />
            {uploading && <CircularProgress />}
          </div>
        </form>
      </Dialog>
    </>
  );
};

export default ComplianceTrainingUploadModal;
